import type { ContactDetailsEventArgs, FormSelectItem, Species } from "../../../types";

/**
 * Emoji and the characters combined emoji are built from: pictographs (U+1F000-U+1FAFF,
 * including flags and skin-tone modifiers), tags (U+E0020-U+E007F), the zero-width joiner,
 * variation selectors, the keycap mark, miscellaneous symbols and dingbats (U+2600-U+27BF) and
 * the other single emoji in the BMP. Keep in sync with `EmojiRegex` in `ProvetInputSanitizer.cs`.
 */
const EMOJI_PATTERN =
  /[\u{1F000}-\u{1FAFF}\u{E0020}-\u{E007F}‍︎️⃣☀-➿‼⁉™ℹ↔-↙↩↪⌚⌛⌨⏏⏩-⏳⏸-⏺Ⓜ▪▫▶◀◻-◾⤴⤵⬅-⬇⬛⬜⭐⭕〰〽㊗㊙]/gu;

/**
 * Removes emoji from text as it is typed, leaving everything else (including spaces) as it is, so
 * a field the customer fills with only emoji is empty and gets its normal validation message.
 */
export const removeEmoji = (text: string): string => text.replace(EMOJI_PATTERN, "");

/**
 * Removes emoji from text, collapsing the spaces they leave behind. The server strips the same
 * characters before it validates the booking, so the summary shows what the clinic receives.
 * Both use the test cases in `tests/fixtures/emoji-strip-cases.json`.
 */
export const stripEmoji = (text: string | undefined): string | undefined => {
  if (!text) {
    return text;
  }

  const stripped = removeEmoji(text);

  return stripped === text ? text : stripped.replace(/[ \t]{2,}/g, " ").trim();
};

/**
 * Strips emoji from the free-text fields the customer typed. Email and phone number are left
 * alone, as they are on the server.
 */
export const stripEmojiFromContactDetails = (
  contactDetails: ContactDetailsEventArgs
): ContactDetailsEventArgs => ({
  ...contactDetails,
  client: {
    ...contactDetails.client,
    firstName: stripEmoji(contactDetails.client.firstName),
    lastName: stripEmoji(contactDetails.client.lastName),
    streetAddress: stripEmoji(contactDetails.client.streetAddress),
    zipCode: stripEmoji(contactDetails.client.zipCode),
    city: stripEmoji(contactDetails.client.city),
    identificationNumber: stripEmoji(contactDetails.client.identificationNumber)
  },
  patient: {
    ...contactDetails.patient,
    name: stripEmoji(contactDetails.patient.name),
    notes: stripEmoji(contactDetails.patient.notes)
  }
});

// Helper function to filter species based on reason species codes
export const filterSpeciesByReasonCodes = (
  allSpecies: Species[],
  reasonSpeciesCodes: number[] | undefined,
  defaultSpecies?: FormSelectItem[]
): FormSelectItem[] => {
  if (reasonSpeciesCodes && reasonSpeciesCodes.length > 0) {
    // Filter species based on reason species codes
    const filteredSpecies = allSpecies.filter(
      s => s?.code !== undefined && reasonSpeciesCodes.includes(s.code)
    );

    // Convert to FormSelectItems
    return filteredSpecies
      .filter(
        speciesItem =>
          speciesItem?.label && speciesItem?.id !== undefined && speciesItem.id !== null
      )
      .map(speciesItem => ({
        caption: speciesItem.label || "",
        value: speciesItem.id?.toString() || ""
      }))
      .filter(item => item.value !== "" && item.caption !== "");
  } else {
    // Use default species if provided, otherwise all species
    if (defaultSpecies && defaultSpecies.length > 0) {
      return defaultSpecies;
    } else {
      return allSpecies
        .filter(
          speciesItem =>
            speciesItem?.label && speciesItem?.id !== undefined && speciesItem.id !== null
        )
        .map(speciesItem => ({
          caption: speciesItem.label || "",
          value: speciesItem.id?.toString() || ""
        }))
        .filter(item => item.value !== "" && item.caption !== "");
    }
  }
};
